import React from 'react';
import { RadialBarChart, RadialBar, PolarAngleAxis, ResponsiveContainer, Tooltip } from 'recharts';

const PowerGauge = ({
  currentPower,
  targetPower,
  remainingTime,
  nextTargetPower,
  maxPower,
  isLast,
  paused,
  height = 320, // can be a number (px) or a string like "100%"
}) => {
  const green = "rgba(50, 200, 50, 1)";
  const red = "rgba(250, 0, 0, 1)";

  // Dynamic values
  const safeTarget = targetPower > 0 ? targetPower : 1;
  const diff = (currentPower - targetPower) / safeTarget;
  const isInRange = Math.abs(diff) <= 0.1;
  const maxDisplayPower = maxPower ? maxPower * 1.2 : 400;

  const currentFill =
    paused || remainingTime <= 0
      ? "url(#idleGlow)"
      : isInRange
        ? "url(#greenGlow)"
        : "url(#redGlow)";

  const targetFill = "url(#targetGlow)";

  const gaugeData = [
    { name: "Target", value: targetPower, fill: targetFill },
    { name: "Current", value: currentPower, fill: currentFill },
  ];

  const isFixedHeight = typeof height === "number";
  const resolvedHeight = isFixedHeight ? `${height}px` : height;

  const panelStyle = {
    width: "100%",
    height: resolvedHeight,
    borderRadius: 18,
    overflow: "hidden",
    border: "1px solid rgba(0, 255, 240, 0.16)",
    boxShadow:
      "0 0 0 1px rgba(255,255,255,0.10) inset, 0 12px 30px rgba(0,0,0,0.22), 0 0 26px rgba(0, 255, 240, 0.10)",
    // Truly transparent background (no fill), only border + glow.
    background: "transparent",
  };

  return (
    <div
      style={{
        marginLeft: 0,
        marginRight: 0,
        marginTop: 0,
        marginBottom: isFixedHeight ? 30 : 0,
        height: resolvedHeight,
      }}
    >
      <div style={panelStyle}>
        <ResponsiveContainer width="100%" height="100%">
          <RadialBarChart
            innerRadius="76%"
            outerRadius="100%"
            barSize={16}
            startAngle={90}
            endAngle={-270}
            data={gaugeData}
            margin={{ top: 0, right: 0, bottom: 0, left: 0 }}
          >
            <defs>
              <linearGradient id="targetGlow" x1="0" y1="0" x2="1" y2="1">
                <stop offset="0%" stopColor="rgba(0, 255, 240, 0.95)" stopOpacity={0.85} />
                <stop offset="55%" stopColor="rgba(0, 160, 255, 0.55)" stopOpacity={0.55} />
                <stop offset="100%" stopColor="rgba(0, 0, 0, 0)" stopOpacity={0.05} />
              </linearGradient>

              <linearGradient id="greenGlow" x1="0" y1="0" x2="1" y2="1">
                <stop offset="0%" stopColor={green} stopOpacity={0.95} />
                <stop offset="55%" stopColor="rgba(0, 255, 240, 0.45)" stopOpacity={0.55} />
                <stop offset="100%" stopColor="rgba(0, 0, 0, 0)" stopOpacity={0.05} />
              </linearGradient>

              <linearGradient id="redGlow" x1="0" y1="0" x2="1" y2="1">
                <stop offset="0%" stopColor={red} stopOpacity={0.95} />
                <stop offset="55%" stopColor="rgba(255, 70, 70, 0.55)" stopOpacity={0.55} />
                <stop offset="100%" stopColor="rgba(0, 0, 0, 0)" stopOpacity={0.05} />
              </linearGradient>

              <linearGradient id="idleGlow" x1="0" y1="0" x2="1" y2="1">
                <stop offset="0%" stopColor="rgba(255,255,255,0.18)" stopOpacity={0.25} />
                <stop offset="60%" stopColor="rgba(0, 255, 240, 0.10)" stopOpacity={0.14} />
                <stop offset="100%" stopColor="rgba(0,0,0,0)" stopOpacity={0.05} />
              </linearGradient>
            </defs>

            {/* PolarAxis hidden for cleaner look */}
            <PolarAngleAxis
              type="number"
              domain={[0, maxDisplayPower]}
              angleAxisId={0}
              tick={false}
            />

            <RadialBar
              minAngle={15}
              background={{ fill: "rgba(0, 0, 65, 0.14)" }}
              clockWise
              dataKey="value"
              cornerRadius={12}
              isAnimationActive={true}
              style={{
                filter:
                  paused || remainingTime <= 0
                    ? "drop-shadow(0 0 8px rgba(0, 255, 240, 0.10))"
                    : isInRange
                      ? "drop-shadow(0 0 10px rgba(50, 200, 50, 0.55)) drop-shadow(0 0 18px rgba(0, 255, 240, 0.18))"
                      : "drop-shadow(0 0 10px rgba(250, 0, 0, 0.55)) drop-shadow(0 0 18px rgba(255, 70, 70, 0.20))",
              }}
            />

            {/* Tooltip for interactivity */}
            <Tooltip
              formatter={(value, name) => `${name}: ${value}W`}
              contentStyle={{
                background: "rgba(6, 8, 20, 0.92)",
                borderRadius: "10px",
                border: "1px solid rgba(0, 255, 240, 0.18)",
                boxShadow: "0 10px 25px rgba(0,0,0,0.45)",
                color: "rgba(0, 0, 65, 0.92)",
              }}
              itemStyle={{ color: "rgba(0, 0, 65, 0.92)" }}
              labelStyle={{ color: "rgba(0, 0, 65, 0.65)" }}
            />

            {/* Centered Text */}
            <text
              x="50%"
              y="50%"
              textAnchor="middle"
              dominantBaseline="middle"
              style={{
                fontSize: "20px",
                fontWeight: 700,
                letterSpacing: "0.06em",
                fill: "rgba(0, 0, 65, 0.88)",
                filter: "drop-shadow(0 0 8px rgba(0, 255, 240, 0.14))",
              }}
            >
              <tspan x="50%" dy="0" fontSize={28}>
                {currentPower} W
              </tspan>
              {paused ? (
                <tspan x="50%" dy="1.25em" fontSize={16} style={{ fill: "rgba(0, 0, 65, 0.65)" }}>
                  PAUSED
                </tspan>
              ) : remainingTime > 0 ? (
                <>
                  <tspan x="50%" dy="1.25em" fontSize={16} style={{ fill: "rgba(0, 0, 65, 0.72)" }}>
                    LOCK ➵ {targetPower} W
                  </tspan>
                  {typeof nextTargetPower === "number" ? (
                    <tspan x="50%" dy="1.15em" fontSize={12} style={{ fill: "rgba(0, 0, 65, 0.50)" }}>
                      NEXT: {nextTargetPower} W
                    </tspan>
                  ) : null}
                </>
              ) : (
                <tspan x="50%" dy="1.25em" fontSize={16} style={{ fill: "rgba(0, 0, 65, 0.72)" }}>
                  WELL DONE
                </tspan>
              )}
            </text>
          </RadialBarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default PowerGauge;